import { merge } from "lodash";
import { useDispatch } from "react-redux";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "../../theme/Theme";
import { AppRoutes } from "../../constants/AppRoutes";
import { Breakpoints } from "../../dto/ApplicationDTO";
import { AuthPromo } from "../../components/auth/AuthPromo";
import { AuthSidebar } from "../../components/auth/AuthSidebar";
import { formatAuthenticateBody } from "../../helpers/AuthHelpers";
import { useCheckCodeMutation, useSendCodeMutation } from "../../api/authApi";
import { changeCode, changeSessionId } from "../../slices/authSlice";
import { useRegistration } from "../../hooks/useRegistration";
import { useLazyGetErrorByStrategyQuery } from "../../api/strategyApi";
import { useAuth } from "../../components/auth/AuthContext";
import { changeModeUser } from "../../slices/applicationSlice";
import {
  ActionDocumentMov,
  BuyStrategyDataProps,
} from "../../components/document/DocumentLookContractByStrategy";
import { createAnswerForSend } from "../../helpers/SurveyHelpers";
import { createUrl } from "../../utils";
import { setAccountType } from "../../slices/profileSlice";
import { useAccountsProfile } from "../../hooks/manageAccountUser/useAccountsProfile";
import { useUserAgreementsQuery } from "../../api/commonApi";

const useStyles = makeStyles((theme) => ({
  root: {
    ...theme.mixins.flexFull,
    ...theme.mixins.flexColumn,
  },
  content: {
    ...theme.mixins.flexFull,
    [theme.breakpoints.down(Breakpoints.Tablet)]: { flexDirection: "column-reverse" },
  },
}));

export function AuthPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { beginInputPersonalData, stepRegistration } = useRegistration();
  const { enterInSystem } = useAuth();
  const [getErrorByStrategy] = useLazyGetErrorByStrategyQuery();
  const agreementsQuery = useUserAgreementsQuery();

  const [sendCodeMutation] = useSendCodeMutation();
  const [checkCodeMutation] = useCheckCodeMutation();
  const { changeAccount } = useAccountsProfile();
  const [authPhone, setAuthPhone] = useState<string | undefined>();
  const [availableAccountTypes, setAvailableAccountTypes] = useState<Auth.AvailableAccountTypes[]>(
    [],
  );
  const [currentAuthStage, setCurrentAuthStage] = useState<
    "auth" | "registration" | "pickAccountType"
  >("auth");

  const handleAvailableAccountTypes = (responseData: Auth.CheckCodeResponseProps) => {
    const accountTypes = responseData?.availableAccountTypes || [];

    if (accountTypes.length === 0) {
      navigate(AppRoutes.DashboardBriefcaseLink);
      return;
    }

    if (accountTypes.length === 1) {
      navigate(AppRoutes.DashboardBriefcaseLink);
      return;
    }

    setAvailableAccountTypes(accountTypes);
    setCurrentAuthStage("pickAccountType");
  };

  const handleAccountTypeSelection = (accountType: Auth.AvailableAccountTypes) => {
    return changeAccount(accountType).then(() => {
      navigate(AppRoutes.DashboardBriefcaseLink);
      setCurrentAuthStage("auth");
    });
  };

  const enterCurrentUser = async (
    values: Auth.AuthenticateBodyProps & Auth.CheckCodeResponseProps,
    isDemo: Store.modeUser,
  ) => {
    setAuthPhone(values.phone);
    enterInSystem({
      code: values.code,
      phone: values.phone,
      modeUser: isDemo,
    });

    // Set the account type if there's only one account type
    if (values.availableAccountTypes && values.availableAccountTypes.length === 1) {
      dispatch(setAccountType(values.availableAccountTypes[0]));
    }

    // eslint-disable-next-line promise/no-nesting
    return await getErrorByStrategy()
      .then((data) => {
        if (data?.data?.data?.idStrategy) {
          navigate(AppRoutes.ProfileLink);
          return;
        }
        handleAvailableAccountTypes(values);
      })
      .catch(() => {
        handleAvailableAccountTypes(values);
      });
  };
  const submitHandler = (values: Auth.AuthenticateBodyProps) => {
    if (values.code) {
      return checkCodeMutation(formatAuthenticateBody(values))
        .unwrap()
        .then(async (data) => {
          const responseCheckCode = data?.data;
          dispatch(changeCode(values.code));
          if (responseCheckCode?.statusUser === "cold") {
            dispatch(changeModeUser("cold"));
            if (responseCheckCode?.checkPointBuyStrategy) {
              switch (responseCheckCode.checkPointBuyStrategy) {
                case "personalData":
                  setAuthPhone(values.phone);
                  beginInputPersonalData(values.phone);
                  break;
                case "oneSurvey":
                  navigate(AppRoutes.SurveyBeforeBuyStrategyLink);
                  break;
                case "twoSurvey":
                  navigate(
                    createUrl(AppRoutes.SurveyAfterBuyStrategyLink, {
                      query: { strategyId: data.data?.strategyId ?? "" },
                    }),
                  );
                  break;
                case "treaty":
                  const beforeFields = await createAnswerForSend("SurveyTestingBeforeBuyStrategy");
                  const afterFields = await createAnswerForSend("SurveyTestingAfterBuyStrategy");
                  const dataForTreaty: BuyStrategyDataProps = {
                    type: ActionDocumentMov.BUY_STRATEGY,
                    strategyId: data.data?.strategyId ?? "",
                    routeBack: AppRoutes.SurveyAfterBuyStrategyLink,
                    stateData: {
                      strategyId: data.data?.strategyId ?? "",
                      stepId: afterFields.stepId,
                      fields: [...beforeFields.fields, ...afterFields.fields],
                    },
                  };
                  navigate(AppRoutes.DocumentRiskStrategyLink, { state: dataForTreaty });
                  break;
                default:
                  handleAvailableAccountTypes(responseCheckCode);
                  break;
              }
              return;
            }
            // await registrationInfoUser()
            //   .unwrap()
            //   .then((dataInfoUser) => {
            //     if (dataInfoUser?.data?.checkPointSaveInfo === "survey") {
            //       setAuthPhone(values.phone);
            //       enterInSystem({
            //         code: values.code,
            //         phone: values.phone,
            //         modeUser: "hot",
            //       });
            //       toHotUserSurvey(dataInfoUser.data.idTargetStrategy);
            //     } else if (dataInfoUser?.data?.checkPointSaveInfo === "personal data") {
            //       enterInSystem({
            //         code: values.code,
            //         phone: values.phone,
            //         modeUser: "cold",
            //       });
            //       setAuthPhone(values.phone);
            //       beginInputPersonalData(values.phone);
            //     } else {
            //       enterCurrentUser(values, "cold");
            //     }
            //   })
            //   .catch(openSnackbarError);
            // return;
            return await getErrorByStrategy()
              .then((data) => {
                if (data?.data?.data?.idStrategy) {
                  navigate(AppRoutes.ProfileLink);
                  return;
                }
                handleAvailableAccountTypes(responseCheckCode);
              })
              .catch(() => {
                handleAvailableAccountTypes(responseCheckCode);
              });
          }
          return enterCurrentUser(merge(values, data?.data), "simple");
        });
    }

    return sendCodeMutation(formatAuthenticateBody(values))
      .unwrap()
      .then((x) => {
        if (x?.sessionId) {
          dispatch(changeSessionId(x.sessionId));
          setAuthPhone(values.phone);
        }
      });
  };
  const toAgreementClick = () => {
    const agreement = agreementsQuery.data?.data?.userAgreement;
    navigate(
      createUrl(AppRoutes.StartAgreement, {
        query: {
          link:
            agreement?.link || `${process.env.REACT_APP_API_HOST}/api/public/agreement/download`,
          title: agreement?.title || "Пользовательское соглашение",
        },
      }),
    );
  };

  const toPrivacyPolicy = () => {
    const agreement = agreementsQuery.data?.data?.privacyPolicy;
    navigate(
      createUrl(AppRoutes.StartAgreement, {
        query: {
          link:
            agreement?.link ||
            "https://www.fbaugust.ru/upload/iblock/33b/4qe11520fodb5aix95yhqs6pibpv4myr.txt",
          title: agreement?.title || "Политика конфиденциальности",
        },
      }),
    );
  };

  const toConsentProcessingPersonalData = async () => {
    const agreement = agreementsQuery.data?.data?.personalDataProcessing;
    navigate(
      createUrl(AppRoutes.StartAgreement, {
        query: {
          link: agreement?.link || "https://be.fbaugust.ru:2841/api/agreement_web",
          title: agreement?.title ?? "Согласие на обработку персональных данных",
        },
      }),
    );
  };

  const handleBackToAuth = () => {
    setCurrentAuthStage("auth");
    setAuthPhone(undefined);
  };

  useEffect(() => {
    dispatch(changeModeUser("simple"));
  }, []);

  const authStage = useMemo(() => {
    if (currentAuthStage === "pickAccountType") {
      return "pickAccountType";
    }

    if (stepRegistration !== 0) {
      return "registration";
    }
    return "auth";
  }, [stepRegistration, currentAuthStage]);

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <AuthPromo />

        <AuthSidebar
          phone={authPhone}
          setPhone={setAuthPhone}
          onSubmit={submitHandler}
          onBackClick={() => setAuthPhone(undefined)}
          onAgreementClick={toAgreementClick}
          onConsentProcessingPersonalData={toConsentProcessingPersonalData}
          onPrivacyPolicy={toPrivacyPolicy}
          authStage={authStage}
          availableAccountTypes={availableAccountTypes}
          onSelectAccountType={handleAccountTypeSelection}
          onBackToAuth={handleBackToAuth}
        />
      </div>
    </div>
  );
}
